import { isStrictDateOnly } from '@agroone/dates'
import { AreaUnit, Regions, SeedingRateUnit } from '@agroone/entities'

/** Columns expected in a crop bulk-import CSV file. */
export const CROP_CSV_IMPORT_HEADERS = [
  'grower',
  'field',
  'cropType',
  'variety',
  'plantingId',
  'plantedDate',
  'area',
  'seedingRate',
  'seedingRateUnit',
  'lotNumber',
  'estimatedHarvestDate',
  'beds',
  'bedWidth',
  'bedWidthUnit',
  'seedLines',
] as const

export type CropCsvImportHeader = (typeof CROP_CSV_IMPORT_HEADERS)[number]
export type CropCsvImportRow = Record<CropCsvImportHeader, string>

/** Why a cell of a crop CSV row is rejected, translated by each caller. */
export const CropCsvImportErrorCode = {
  REQUIRED: 'REQUIRED',
  INVALID_DATE: 'INVALID_DATE',
  INVALID_NUMBER: 'INVALID_NUMBER',
  INVALID_INTEGER: 'INVALID_INTEGER',
  INVALID_SEEDING_RATE_UNIT: 'INVALID_SEEDING_RATE_UNIT',
  INVALID_BED_WIDTH_UNIT: 'INVALID_BED_WIDTH_UNIT',
  HARVEST_BEFORE_PLANTING: 'HARVEST_BEFORE_PLANTING',
} as const

export type CropCsvImportErrorCode = (typeof CropCsvImportErrorCode)[keyof typeof CropCsvImportErrorCode]

export interface CropCsvImportCellError {
  field: CropCsvImportHeader
  code: CropCsvImportErrorCode
}

const REQUIRED_FIELDS: CropCsvImportHeader[] = [
  'grower',
  'field',
  'cropType',
  'variety',
  'plantingId',
  'plantedDate',
  'area',
  'seedingRate',
  'seedingRateUnit',
]

/** Decimal columns and the number of decimals their database column keeps. */
const DECIMAL_FIELDS: Partial<Record<CropCsvImportHeader, number>> = {
  area: 4,
  seedingRate: 2,
  bedWidth: 2,
}

const INTEGER_FIELDS: CropCsvImportHeader[] = ['beds', 'seedLines']

const DATE_FIELDS: CropCsvImportHeader[] = ['plantedDate', 'estimatedHarvestDate']

const BED_WIDTH_UNITS = ['in', 'cm', 'mm'] as const
const DEFAULT_BED_WIDTH_UNIT = 'in'

const SEEDING_RATE_UNITS: string[] = Object.values(SeedingRateUnit)

const INTEGER_RE = /^\d+$/

// Month/day/year, the way Excel saves a date cell in a US locale, e.g. 9/4/2026
const US_DATE_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/

/** A US M/D/YYYY date as YYYY-MM-DD, any other value unchanged (left for the validation to reject). */
const normalizeDate = (value: string): string => {
  const match: RegExpExecArray | null = US_DATE_RE.exec(value)
  if (!match) return value
  const [, month, day, year] = match
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

const isPositiveDecimal = (value: string, maxDecimals: number): boolean =>
  new RegExp(`^\\d+(\\.\\d{1,${maxDecimals}})?$`).test(value) && Number(value) > 0

const isPositiveInteger = (value: string): boolean => INTEGER_RE.test(value) && Number(value) >= 1

/**
 * Business rules governing the CSV bulk-import of crops: which region may use it, the
 * columns of the file, how each cell is validated and the values the file can't carry.
 * Crops are created already planted, from the farm, field, crop type and variety names
 * an external source knows them by.
 */
export const CropCsvImportRules = {
  headers: CROP_CSV_IMPORT_HEADERS,

  requiredFields: REQUIRED_FIELDS,

  /** The CSV imports crops of the AMERICA region only. */
  region: Regions.AMERICA,

  /** Areas of the file are in acres. */
  areaUnit: AreaUnit.ac,

  /** Whether the users of the region can import crops by CSV. */
  isBulkImportAvailableForRegion: (regionName: string | null | undefined): boolean => regionName === Regions.AMERICA,

  isFieldRequired: (header: string): boolean => (REQUIRED_FIELDS as string[]).includes(header),

  seedingRateUnits: (): string[] => SEEDING_RATE_UNITS,

  bedWidthUnits: (): readonly string[] => BED_WIDTH_UNITS,

  /** The bed width unit of a row, inches when the cell is empty. */
  resolveBedWidthUnit: (value: string | null | undefined): string => value || DEFAULT_BED_WIDTH_UNIT,

  /** CSV dates are date-only, e.g. 2026-05-12. */
  isValidDateFormat: (value: string): boolean => isStrictDateOnly(value),

  normalizeDate,

  /**
   * The row with its dates as YYYY-MM-DD. The file may also carry US M/D/YYYY dates: the import
   * is for AMERICA only, and Excel rewrites the dates of a US file that way when saving it.
   * To apply before `getRowErrors`.
   */
  normalizeRow: (row: CropCsvImportRow): CropCsvImportRow => {
    const normalized: CropCsvImportRow = { ...row }
    for (const field of DATE_FIELDS) {
      normalized[field] = normalizeDate(row[field])
    }
    return normalized
  },

  /**
   * Validates the cells of a row on their own, before any lookup: required cells, formats
   * and allowed units. Expects trimmed values, an empty string standing for an empty cell.
   */
  getRowErrors: (row: CropCsvImportRow): CropCsvImportCellError[] => {
    const errors: CropCsvImportCellError[] = []

    for (const field of REQUIRED_FIELDS) {
      if (!row[field]) errors.push({ field, code: CropCsvImportErrorCode.REQUIRED })
    }

    for (const field of DATE_FIELDS) {
      if (row[field] && !isStrictDateOnly(row[field])) errors.push({ field, code: CropCsvImportErrorCode.INVALID_DATE })
    }

    for (const [field, maxDecimals] of Object.entries(DECIMAL_FIELDS) as [CropCsvImportHeader, number][]) {
      if (row[field] && !isPositiveDecimal(row[field], maxDecimals)) {
        errors.push({ field, code: CropCsvImportErrorCode.INVALID_NUMBER })
      }
    }

    for (const field of INTEGER_FIELDS) {
      if (row[field] && !isPositiveInteger(row[field])) {
        errors.push({ field, code: CropCsvImportErrorCode.INVALID_INTEGER })
      }
    }

    if (row.seedingRateUnit && !SEEDING_RATE_UNITS.includes(row.seedingRateUnit)) {
      errors.push({ field: 'seedingRateUnit', code: CropCsvImportErrorCode.INVALID_SEEDING_RATE_UNIT })
    }

    if (row.bedWidthUnit && !(BED_WIDTH_UNITS as readonly string[]).includes(row.bedWidthUnit)) {
      errors.push({ field: 'bedWidthUnit', code: CropCsvImportErrorCode.INVALID_BED_WIDTH_UNIT })
    }

    // Both are date-only YYYY-MM-DD strings: they compare as text
    if (
      isStrictDateOnly(row.plantedDate) &&
      row.estimatedHarvestDate &&
      isStrictDateOnly(row.estimatedHarvestDate) &&
      row.estimatedHarvestDate < row.plantedDate
    ) {
      errors.push({ field: 'estimatedHarvestDate', code: CropCsvImportErrorCode.HARVEST_BEFORE_PLANTING })
    }

    return errors
  },
} as const
